import { createSlice } from '@reduxjs/toolkit';

import {
  COMPLETE_NEW_PASSWORD_SUBMIT,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_RESEND_OTP,
  FORGOT_PASSWORD_SUBMIT,
  GET_CURRENT_USER,
  GET_CURRENT_USER_ACCOUNT_INFO,
  SIGNIN,
  SIGNOUT,
  UPDATE_USER_PROFILE_DATA
} from 'redux/sagas/actions/authentication';

export const FORGOT_STEPS = {
  newPasswordSuccess: 'newPasswordSuccess',
  forgotPassword: 'forgotPassword',
  createPassword: 'createPassword',
  insertCode: 'insertCode',
  changePassword: 'changePassword',
  empty: 'login'
};

const initialState = {
  steps_history: [FORGOT_STEPS.empty],
  forgotStep: FORGOT_STEPS.empty,
  redirectToCreatePassword: false,
  isLoadingUser: true,
  errorOtp: null,
  errorPswDontMatch: null,
  errorPswNotValid: null,
  isLoading: false
};

const blankError = state => {
  state.errorMessage = null;
};

const setUser = (state, action) => {
  state.user = action.payload;
  state.isLoadingUser = false;
};

const setError = (state, action) => {
  state.errorMessage = action.payload;
  state.errorOtp = action.payload;
};

const setRole = (state, action) => {
  const { role } = action.payload;
  return {
    ...state,
    role,
    accountInfo: action.payload
  };
};

export const authenticationSlice = createSlice({
  name: '@@authentication',
  initialState,
  reducers: {
    SET_ACCESS_ERROR: setError,
    SET_ACCESS_ERROR_OTP: (state, action) => ({
      ...state,
      errorOtp: action.payload
    }),
    SET_ACCESS_ERROR_PASSWORD_NOT_MATCH: (state, action) => ({
      ...state,
      errorPswDontMatch: action.payload
    }),
    SET_ACCESS_ERROR_PASSWORD_NOT_VALID: (state, action) => ({
      ...state,
      errorPswNotValid: action.payload
    }),
    RESET_ERRORS: state => ({
      ...state,
      errorOtp: null,
      errorPswNotValid: null,
      errorPswDontMatch: null
    }),
    SET_PREVIOUS_STEP: (state, action) => {
      const { steps_history } = state;
      const historyLength = steps_history.length;
      steps_history.pop(state.steps_history[historyLength - 1]);
      state.forgotStep = action.payload;
    },
    SET_FORGOT_STEP_EMPTY: () => ({ ...initialState }),
    SET_FORGOT_STEP: state => {
      state.errorMessage = null;
      const { steps_history } = state;
      steps_history.push(FORGOT_STEPS.forgotPassword);
      state.forgotStep = FORGOT_STEPS.forgotPassword;
    },
    FORGOT_VERIFICATION_CODE: (state, action) => {
      // state.forgotStep = FORGOT_STEPS.createPassword;
      state.errorMessage = null;
      state.forgotData = {
        ...(state.forgotData || {}),
        code: action.payload
      };
    },
    FORCE_PASSWORD_CHANGE: (state, action) => {
      const { user, requiredAttributes } = action.payload;
      return {
        ...state,
        completeNewPasswordData: {
          user,
          requiredAttributes
        },
        redirectToCreatePassword: true
      };
    },
    RESET_STATE: () => initialState,
    RESEND_OTP_IS_LOADING: state => ({
      ...state,
      isLoading: true
    }),
    RESEND_OTP_IS_NOT_LOADING: state => ({
      ...state,
      isLoading: false
    })
  },
  extraReducers: {
    [SIGNIN._REQUEST]: blankError,
    [FORGOT_PASSWORD._REQUEST]: blankError,
    [FORGOT_PASSWORD_SUBMIT._REQUEST]: blankError,
    [SIGNIN._ERROR]: setError,
    [FORGOT_PASSWORD._ERROR]: setError,
    [FORGOT_PASSWORD_SUBMIT._ERROR]: setError,
    [SIGNIN._SUCCESS]: setUser,
    [GET_CURRENT_USER._SUCCESS]: setUser,
    [FORGOT_PASSWORD._SUCCESS]: (state, action) => {
      const { steps_history } = state;
      steps_history.push(FORGOT_STEPS.changePassword);
      state.forgotStep = FORGOT_STEPS.changePassword;
      state.errorMessage = null;
      state.forgotData = {
        ...(state.forgotData || {}),
        userName: action.payload
      };
    },
    [FORGOT_PASSWORD_RESEND_OTP._SUCCESS]: (state, action) => {
      const { steps_history } = state;
      steps_history.push(FORGOT_STEPS.changePassword);
      state.forgotStep = FORGOT_STEPS.changePassword;
      state.errorMessage = null;
      state.forgotData = {
        ...(state.forgotData || {}),
        userName: action.payload
      };
    },
    [FORGOT_PASSWORD_RESEND_OTP._ERROR]: (state, action) => ({
      ...state,
      errorOtp: action.payload
    }),
    [FORGOT_PASSWORD_SUBMIT._SUCCESS]: state => {
      state.forgotStep = FORGOT_STEPS.newPasswordSuccess;
      state.errorMessage = null;
      state.errorPswDontMatch = null;
      state.errorPswNotValid = null;
      state.errorOtp = null;
      state.forgotData = null;
    },
    [SIGNOUT._SUCCESS]: () => initialState,
    [GET_CURRENT_USER_ACCOUNT_INFO._SUCCESS]: setRole,
    [COMPLETE_NEW_PASSWORD_SUBMIT._SUCCESS]: setUser,
    [COMPLETE_NEW_PASSWORD_SUBMIT._ERROR]: setError,
    [COMPLETE_NEW_PASSWORD_SUBMIT._REQUEST]: blankError,
    [GET_CURRENT_USER._REQUEST]: state => ({ ...state, isLoadingUser: true }),
    [GET_CURRENT_USER._ERROR]: state => ({ ...state, isLoadingUser: false }),
    [UPDATE_USER_PROFILE_DATA._SUCCESS]: (state, action) => ({
      ...state,
      user: {
        ...state.user,
        phoneNumber: action.payload.phoneNumber,
        name: action.payload.name,
        lastName: action.payload.lastName
      }
    })
  }
});

export const {
  SET_ACCESS_ERROR,
  FORGOT_VERIFICATION_CODE,
  SET_ACCESS_ERROR_PASSWORD_NOT_MATCH,
  SET_ACCESS_ERROR_PASSWORD_NOT_VALID,
  SET_ACCESS_ERROR_OTP,
  FORCE_PASSWORD_CHANGE,
  SET_FORGOT_STEP,
  SET_FORGOT_STEP_EMPTY,
  SET_PREVIOUS_STEP,
  REDIRECT_TO_LOGIN,
  RESET_STATE,
  RESET_ERRORS,
  RESEND_OTP_IS_LOADING,
  RESEND_OTP_IS_NOT_LOADING
} = authenticationSlice.actions;

export default authenticationSlice.reducer;
